import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIsActive, useIsLayerTerminal } from '../../../components/Others/layerControl/hooks';
import { useIsActiveByName, useSettingsByName } from '../../hooks';
import { disableScreenSaver, enableScreenSaver } from '../../../global-state/redux/actions';
import { useIsUserLoggedIn } from '../../../global-utils';
import { loginInfoFromStorage } from '../../../global-state/redux/actions/utils';

// Remove after EHA2026
const EHA2026_FLOORPLAN_TO_SCREENSAVER = {
  '/venue-floorplan': '/venue-screensaver',
  '/exhibitor-floorplan': '/exhibitor-screensaver',
  '/exhibitor-floorplan1': '/exhibitor-screensaver1',
  '/exhibitor-floorplan2': '/exhibitor-screensaver2',
  '/exhibitor-floorplan3': '/exhibitor-screensaver3'
};
// Remove after EHA2026
const EHA2026_SCREENSAVER_TO_FLOORPLAN = Object.fromEntries(
  Object.entries(EHA2026_FLOORPLAN_TO_SCREENSAVER).map(([k, v]) => [v, k])
);

const isEha2026Site = window.location.origin === 'https://eha2026.ehaweb.org'; // Remove after EHA2026
// Remove after EHA2026
const ScreenSaverWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const screenSaverMode = useSelector((state) => state.appState?.screenSaverMode);
  const loginFromStorage = !!loginInfoFromStorage();
  const isUserLoggedIn = useIsUserLoggedIn();
  const isAuthLoading = useSelector((state) => state.appState?.isAuthLoading);

  const layerControlIsActive = useIsActive();
  const isTerminalActive = useIsActiveByName('terminal');
  const isLayerTerminal = useIsLayerTerminal();
  const isTerminalMode = Boolean(layerControlIsActive && isTerminalActive && isLayerTerminal);

  const settings = useSettingsByName('terminal') ?? {};
  const screenSaver = settings?.screenSaver ?? {};
  const { startPage } = settings;
  const { startAfterInSeconds, screenSaverPath } = screenSaver ?? {};

  const timerRef = useRef(null);
  const hasNavigatedToStartPageRef = useRef(false);

  // Handle inactivity timer for screen saver
  useEffect(() => {
    if (!isTerminalMode || !startAfterInSeconds || startAfterInSeconds < 0) return () => {};

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        dispatch(enableScreenSaver());
      }, startAfterInSeconds * 1000);
    };

    const handleActivity = () => {
      if (screenSaverMode) {
        dispatch(disableScreenSaver());
      }
      resetTimer();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((event) => window.addEventListener(event, handleActivity));

    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [dispatch, isTerminalMode, startAfterInSeconds, screenSaverMode]);

  // Redirect to login if not logged in (only in terminal mode)
  useEffect(() => {
    if (!isTerminalMode || isAuthLoading) return;

    if (!isUserLoggedIn && !loginFromStorage) {
      if (window.location.pathname !== '/auth/login') {
        navigate('/auth/login', { replace: true });
      }
    }
  }, [isTerminalMode, isAuthLoading, isUserLoggedIn, loginFromStorage, navigate]);

  // Reset startPage navigation flag when exiting terminal mode
  useEffect(() => {
    if (!isTerminalMode) hasNavigatedToStartPageRef.current = false;
  }, [isTerminalMode]);

  // Navigate to startPage when entering terminal mode
  useEffect(() => {
    if (!isTerminalMode) return;

    if (!isUserLoggedIn) return;

    if (!isEha2026Site && startPage && !hasNavigatedToStartPageRef.current) {
      hasNavigatedToStartPageRef.current = true;
      navigate(startPage, { replace: true });
    }
  }, [isTerminalMode, startPage, navigate, isUserLoggedIn]);

  // Handle screen saver path navigation (only in terminal mode)
  useEffect(() => {
    if (!isTerminalMode) return;

    if (isEha2026Site) {
      if (screenSaverMode) {
        const ehaScreensaverPath = EHA2026_FLOORPLAN_TO_SCREENSAVER[window.location.pathname];
        if (ehaScreensaverPath) {
          navigate(ehaScreensaverPath, { replace: true });
        }
      } else {
        const ehaFloorplanPath = EHA2026_SCREENSAVER_TO_FLOORPLAN[window.location.pathname];
        if (ehaFloorplanPath) {
          navigate(ehaFloorplanPath, { replace: true });
        }
      }
      return;
    }

    if (screenSaverMode && screenSaverPath) {
      navigate(screenSaverPath, { replace: true });
    }

    if (!screenSaverMode && window.location.pathname === screenSaverPath) {
      navigate(startPage, { replace: true });
    }
  }, [isTerminalMode, screenSaverMode, screenSaverPath, startPage, navigate]);

  return children;
};

export default ScreenSaverWrapper;
